import React from "react"
import autobind from "class-autobind"
import strings from "../../localization/strings"
import { ELanguage, IQuestion, ILanguageObj, IQuestionDocument, IQuestionIntroduction, IQuestionExceptionConfirm } from "../../models"
import { Dialog, FlatButton, CircularProgress } from "material-ui"
import _ from "lodash"
import LanguageSelectChip from "../language/LanguageSelectChip"
import QuestionIntroduction from "./QuestionIntroduction"
import PunchQuestionBody from "./PunchQuestionBody"
import AppStateStore from "../../stores/AppStateStore"
import * as utils from "../../helpers/utils"
import AccountStore from "../../stores/AccountStore"
import * as api from "../../api/api"
import QuestionExceptionConfirm from "./QuestionExceptionConfirm"
import he from "he"

export enum OpenPunchQuestionDocDialogMode {
    View,
    Add,
    Edit
}
interface IProps {
    openPunchQuestionDocDialogMode: OpenPunchQuestionDocDialogMode
    questionDocId: string
    handleSave(questionDocId: string): void
    handleClose(): void
}
interface IState {
    loading: boolean
    selectedLanguages: ELanguage[]
    allowIntroPunchIn: boolean
    punchInIntroductions: IQuestionIntroduction[]
    allowIntroPunchOut: boolean
    punchOutIntroductions: IQuestionIntroduction[]
    questions: IQuestion[]
    exceptionConfirms: IQuestionExceptionConfirm[]
}

export default class PunchQuestionDocDialog extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props)
        this.state = {
            loading: props.openPunchQuestionDocDialogMode !== OpenPunchQuestionDocDialogMode.Add,
            selectedLanguages: [ELanguage.English],
            allowIntroPunchIn: false,
            punchInIntroductions: [],
            allowIntroPunchOut: false,
            punchOutIntroductions: [],
            questions: [],
            exceptionConfirms: []
        }
        autobind(this)
    }

    componentDidMount() {
        this.props.openPunchQuestionDocDialogMode !== OpenPunchQuestionDocDialogMode.Add &&
            api.getQuestion(this.props.questionDocId).then(res => {
                const questionDoc = res.data as IQuestionDocument
                questionDoc.Questions.forEach(question => {
                    if (question.Body)
                        question.Body = he.decode(question.Body)

                    if (question.BtnLabel1)
                        question.BtnLabel1 = he.decode(question.BtnLabel1)

                    if (question.BtnLabel2)
                        question.BtnLabel2 = he.decode(question.BtnLabel2)

                    if (question.NotifyMsg)
                        question.NotifyMsg = he.decode(question.NotifyMsg)
                })
                questionDoc.ExceptionConfirm.forEach(i => {
                    if (i.BtnLabel)
                        i.BtnLabel = he.decode(i.BtnLabel)

                    if (i.Title)
                        i.Title = he.decode(i.Title)
                })
                questionDoc.Introduction.forEach(i => {
                    if (i.Body)
                        i.Body = he.decode(i.Body)

                    if (i.BtnLabel)
                        i.BtnLabel = he.decode(i.BtnLabel)

                    if (i.Title)
                        i.Title = he.decode(i.Title)
                })
                const selectedLanguages = questionDoc.PreQuestionLanguages.map(i => i.Id)
                const punchInIntroductions = questionDoc.Introduction.filter(i => i.PunchIn)
                const punchOutIntroductions = questionDoc.Introduction.filter(i => !i.PunchIn)
                this.setState({
                    loading: false,
                    selectedLanguages,
                    allowIntroPunchIn: punchInIntroductions.length > 0,
                    punchInIntroductions,
                    allowIntroPunchOut: punchOutIntroductions.length > 0,
                    punchOutIntroductions,
                    questions: questionDoc.Questions || [],
                    exceptionConfirms: questionDoc.ExceptionConfirm || []
                })
            })
    }

    handleSave() {
        const errors = this.checkValidate()
        if (errors.length) {
            AppStateStore.setAlertDialogContent({
                title: strings.Error,
                content: errors.length === 1 ? errors[0] : errors
            })
            return
        }

        const preQuestionLanguages: ILanguageObj[] = this.state.selectedLanguages.map(i => {
            const langObj: ILanguageObj = {
                Id: i,
                Name: ELanguage[i]
            }
            return langObj
        })

        const introductions: IQuestionIntroduction[] = this.state.punchInIntroductions.concat(this.state.punchOutIntroductions)
        this.state.questions.forEach(i => i.GUId = utils.guid())
        const questionDocument: IQuestionDocument = {
            Id: utils.guid(),
            AccountId: AccountStore.getCurrentAccount().Id,
            PreQuestionLanguages: preQuestionLanguages,
            Introduction: introductions,
            Questions: this.state.questions,
            ExceptionConfirm: this.state.exceptionConfirms,
            UserModified: true
        }
        AppStateStore.setProgressSnackbarContent("loading")
        api.addQuestion(questionDocument).then(() => {
            this.props.handleSave(questionDocument.Id)
        })
    }

    checkValidate(): string[] {
        let errors: string[] = []
        const langCount = this.state.selectedLanguages.length
        if (this.state.questions.length) {
            let questionValid = true
            const punchInQuestions = this.state.questions.filter(i => i.PunchIn)
            if (punchInQuestions.length) {
                const groupIds = punchInQuestions.map(i => i.GroupId)
                const uniqueGroupIds = groupIds.filter((id, index) => groupIds.indexOf(id) === index)
                uniqueGroupIds.map(groupId => {
                    if (punchInQuestions.filter(i => i.GroupId === groupId).length !== langCount)
                        questionValid = false
                })
            }

            const punchOutQuestions = this.state.questions.filter(i => !i.PunchIn)
            if (punchOutQuestions.length) {
                const groupIds = punchOutQuestions.map(i => i.GroupId)
                const uniqueGroupIds = groupIds.filter((id, index) => groupIds.indexOf(id) === index)
                uniqueGroupIds.map(groupId => {
                    if (punchOutQuestions.filter(i => i.GroupId === groupId).length !== langCount)
                        questionValid = false
                })
            }

            !questionValid &&
                errors.push(strings.Some_questions_are_missing_information_for_the_selected_languages)

            punchInQuestions.length > 0 &&
                punchInQuestions.filter(i => !i.Child).length === 0 &&
                errors.push(strings.Parent_question_not_exist_in_Punch_In)

            punchOutQuestions.length > 0 &&
                punchOutQuestions.filter(i => !i.Child).length === 0 &&
                errors.push(strings.Parent_question_not_exist_in_Punch_Out)
        }
        else {
            errors.push(strings.Empty_question)
        }

        this.state.allowIntroPunchIn &&
            (!this.state.punchInIntroductions.length || this.state.punchInIntroductions.length !== langCount) &&
            errors.push(strings.PunchIn_introduction_is_not_valid)

        this.state.allowIntroPunchOut &&
            (!this.state.punchOutIntroductions.length || this.state.punchOutIntroductions.length !== langCount) &&
            errors.push(strings.PunchOut_introduction_is_not_valid)

        this.state.exceptionConfirms.length !== langCount &&
            errors.push(strings.Exception_Confirm_Screen_is_not_valid)
        return errors
    }

    handleSortByChild(questions: IQuestion[]): IQuestion[] {
        const rootQuestionGroupIds = questions.filter(i => !i.Child && i.Language === ELanguage.English).map(i => i.GroupId)
        const childQuestionGroupIds = questions.filter(i => i.Child && i.Language === ELanguage.English).map(i => i.GroupId)
        const newGroupIds = []
        rootQuestionGroupIds.forEach(i =>
            newGroupIds.push(i)
        )
        childQuestionGroupIds.forEach(i =>
            newGroupIds.push(i)
        )

        return questions
            .map(i => {
                i.GroupId = newGroupIds.indexOf(i.GroupId)
                if (i.Btn1Action >= 0) {
                    i.Btn1Action = newGroupIds.indexOf(i.Btn1Action)
                }
                if (i.Btn2Action >= 0) {
                    i.Btn2Action = newGroupIds.indexOf(i.Btn2Action)
                }
                return i
            })
            .sort((a, b) => {
                if (a.Language === b.Language) return 0
                if (a.Language < b.Language) return -1
                if (a.Language > b.Language) return 1
            })
            .sort((a, b) => {
                if (a.GroupId === b.GroupId) return 0
                if (a.GroupId < b.GroupId) return -1
                if (a.GroupId > b.GroupId) return 1
            })
    }

    render() {
        const readonly = this.props.openPunchQuestionDocDialogMode === OpenPunchQuestionDocDialogMode.View
        return (
            <Dialog
                open
                contentClassName="PunchQuestionDocDialog"
                title={this.props.openPunchQuestionDocDialogMode === OpenPunchQuestionDocDialogMode.View ?
                    strings.View_Question :
                    this.props.openPunchQuestionDocDialogMode === OpenPunchQuestionDocDialogMode.Edit ?
                        strings.Edit_Question :
                        strings.Add_Question
                }
                contentStyle={{ width: "80%", maxWidth: "none" }}
                actions={this.props.openPunchQuestionDocDialogMode === OpenPunchQuestionDocDialogMode.View ?
                    <FlatButton label={strings.Close} primary onTouchTap={this.props.handleClose} /> :
                    [
                        <FlatButton data-intercom-target={"PunchQuestion-Btn-CloseAddPunchQuestionDialog"} key="cancel" label={strings.CANCEL} secondary onTouchTap={this.props.handleClose} />,
                        <FlatButton data-intercom-target={"PunchQuestion-Btn-SavePunchQuestions"} key="save" label={strings.SAVE} primary onTouchTap={this.handleSave} />
                    ]
                }
                autoScrollBodyContent
            >
                {this.state.loading ?
                    <div style={{ textAlign: "center" }}>
                        <CircularProgress />
                    </div> :
                    <div>
                        <div style={{ paddingLeft: 20, paddingRight: 20 }}>
                            <LanguageSelectChip
                                selectedLanguages={this.state.selectedLanguages}
                                readonly={readonly}
                                error={""}
                                handleChange={selectedLanguages => {
                                    const punchInIntroductions = this.state.punchInIntroductions.filter(i => selectedLanguages.includes(i.Language))
                                    const punchOutIntroductions = this.state.punchOutIntroductions.filter(i => selectedLanguages.includes(i.Language))
                                    const questions = this.state.questions.filter(i => selectedLanguages.includes(i.Language))
                                    const exceptionConfirms = this.state.exceptionConfirms.filter(i => selectedLanguages.includes(i.Language))
                                    this.setState({ selectedLanguages, punchInIntroductions, punchOutIntroductions, questions, exceptionConfirms })
                                }}
                            />
                            <QuestionIntroduction
                                punchIn
                                readonly={readonly}
                                selectedLanguages={this.state.selectedLanguages}
                                toggleTitle={strings.Include_introduction_page_message_Punch_In}
                                allowIntro={this.state.allowIntroPunchIn}
                                introductions={this.state.punchInIntroductions}
                                handleChangeAllowIntro={allowIntroPunchIn => this.setState({ allowIntroPunchIn, punchInIntroductions: allowIntroPunchIn ? this.state.punchInIntroductions : [] })}
                                handleChangeIntro={punchInIntroductions => this.setState({ punchInIntroductions })}
                            />
                            <QuestionIntroduction
                                punchIn={false}
                                readonly={readonly}
                                selectedLanguages={this.state.selectedLanguages}
                                toggleTitle={strings.Include_introduction_page_message_Punch_Out}
                                allowIntro={this.state.allowIntroPunchOut}
                                introductions={this.state.punchOutIntroductions}
                                handleChangeAllowIntro={allowIntroPunchOut => this.setState({ allowIntroPunchOut, punchOutIntroductions: allowIntroPunchOut ? this.state.punchOutIntroductions : [] })}
                                handleChangeIntro={punchOutIntroductions => this.setState({ punchOutIntroductions })}
                            />
                            <QuestionExceptionConfirm
                                readonly={readonly}
                                selectedLanguages={this.state.selectedLanguages}
                                exceptionConfirms={this.state.exceptionConfirms}
                                handleChange={exceptionConfirms => this.setState({ exceptionConfirms })}
                            />
                        </div>
                        <PunchQuestionBody
                            selectedLanguages={this.state.selectedLanguages}
                            readonly={readonly}
                            questions={this.state.questions}
                            handleChange={changedQuestions => {
                                const punchInQuestions = this.handleSortByChild(changedQuestions.filter(i => i.PunchIn))
                                const punchOutQuestions = this.handleSortByChild(changedQuestions.filter(i => !i.PunchIn))
                                const questions = [...punchInQuestions, ...punchOutQuestions]
                                this.setState({ questions })
                            }}
                        />
                    </div>
                }
            </Dialog >
        )
    }
}
